import { useState } from "react";
import { TYPE_META } from "@/lib/format";
import {
  BUSINESS_TYPES, businessTypeLabel, COMM_OPTIONS, TYPE_QUESTIONS, GOAL_OPTIONS, goalLabel,
  EXPERIENCE_ONB, TYPE_ONB, GOAL_ONB, ACQ_OPTIONS, ONB_NETWORKS, splitChips, joinChips,
  needsLocalClientNote, LOCAL_CLIENT_NOTE, LOCAL_RECOMMENDER_GOAL,
} from "@/lib/onboardingConfig";
import { CaSelect } from "./CaSelect";
import { MarketField, marketSummary } from "./MarketField";
import { ProfileCard } from "./ProfileCards";
import { profileLocationLabel } from "./profiles";

export const Option = ({ selected, title, hint, onClick, testId }) => (
  <button type="button" className={`onb-option ${selected ? "selected" : ""}`} onClick={onClick} data-testid={testId} aria-pressed={selected}>
    <span className="mark">{selected ? "✓" : ""}</span>
    <span><b>{title}</b>{hint && <small>{hint}</small>}</span>
  </button>
);

const Chip = ({ on, children, onClick, testId }) => (
  <button type="button" className={`onb-chip ${on ? "selected" : ""}`} aria-pressed={on} onClick={onClick} data-testid={testId}>{children}</button>
);

const Opt = () => <span className="onb-field-optional">Opcional</span>;
const Ess = () => <span className="onb-essential-tag">Esencial</span>;
const Err = ({ msg }) => (msg ? <span className="ca-field-error" role="alert">{msg}</span> : null);

const Page = ({ eyebrow, title, intro, children }) => (
  <div className="onb-page">
    <span className="eyebrow">{eyebrow}</span>
    <h1>{title}</h1>
    {intro && <p className="onb-intro">{intro}</p>}
    {children}
  </div>
);

const Note = ({ children, testId }) => (
  <div className="onb-optional" data-testid={testId}><div className="onb-optional-body"><p className="onb-optional-note"><i>✦</i><span>{children}</span></p></div></div>
);

export const Step1 = ({ biz, setB, errors }) => (
  <Page eyebrow="Antes de empezar" title="¿Qué experiencia tienes buscando y contactando personas?" intro="Así ajustamos el nivel de orientación. No es una evaluación y puedes cambiarlo después.">
    <div className="onb-options">
      {Object.entries(EXPERIENCE_ONB).map(([k, [t, h]]) => (
        <Option key={k} testId={`onb-exp-${k}`} selected={biz.experience_level === k} title={t} hint={h} onClick={() => setB("experience_level", k)} />
      ))}
    </div>
    <Err msg={errors.experience_level} />
  </Page>
);

export const Step2 = ({ biz, setB, setQ, errors }) => (
  <Page eyebrow="Tu actividad" title="¿A qué te dedicas?" intro="Elige el modelo que mejor encaje. Adaptaremos las preguntas sin cambiar el resto del recorrido.">
    <div className="onb-options">
      {BUSINESS_TYPES.map((t) => {
        const [title, hint] = TYPE_ONB[t.k] || [t.label, ""];
        const sel = biz.business_type === t.k;
        const opt = <Option key={t.k} testId={`onb-type-${t.k}`} selected={sel} title={title} hint={hint} onClick={() => setB("business_type", t.k)} />;
        if (t.k !== "otro") return opt;
        return (
          <div key={t.k} className={`onb-other-wrap ${sel ? "selected" : ""}`}>
            {opt}
            {sel && (
              <div className="onb-other-field">
                <input autoFocus data-testid="onb-other-activity" aria-label="Describe tu actividad" placeholder="Escribe aquí tu actividad"
                  value={biz.questionnaire.other_activity || ""} onChange={(e) => setQ("other_activity", e.target.value)} />
              </div>
            )}
          </div>
        );
      })}
    </div>
    <Err msg={errors.business_type} />
  </Page>
);

const Question = ({ q, biz, setQ, errors, essential }) => {
  const val = biz.questionnaire[q.k] || "";
  const bad = errors[q.k] ? "is-invalid" : "";
  return (
    <div className={`field ${q.type === "textarea" ? "wide" : ""}`}>
      <label>{q.label} {essential ? <Ess /> : <Opt />}</label>
      {q.type === "textarea" ? (
        <textarea data-testid={`onb-q-${q.k}`} className={bad} value={val} maxLength={3000} placeholder="Añade contexto si lo sabes" onChange={(e) => setQ(q.k, e.target.value)} />
      ) : q.type === "select" ? (
        <CaSelect testId={`onb-q-${q.k}`} options={q.options.map((o) => ({ value: o.k, label: o.label }))} value={val} placeholder="Elige una opción…" onChange={(v) => setQ(q.k, v)} />
      ) : (
        <input data-testid={`onb-q-${q.k}`} className={bad} value={val} maxLength={600} placeholder="Añade una respuesta breve" onChange={(e) => setQ(q.k, e.target.value)} />
      )}
      {q.hint && !errors[q.k] && <span className="hint">{q.hint}</span>}
      <Err msg={errors[q.k]} />
    </div>
  );
};

const AcqChips = ({ value, onChange }) => {
  const { chips, other } = splitChips(value, ACQ_OPTIONS);
  const [showOther, setShowOther] = useState(!!other);
  const toggle = (c) => onChange(joinChips(chips.includes(c) ? chips.filter((x) => x !== c) : [...chips, c], other));
  return (
    <div className="onb-chips">
      {ACQ_OPTIONS.map((c) => <Chip key={c} on={chips.includes(c)} onClick={() => toggle(c)} testId={`onb-acq-${c}`}>{c}</Chip>)}
      <Chip on={showOther} onClick={() => { if (showOther) onChange(joinChips(chips, "")); setShowOther(!showOther); }} testId="onb-acq-otro">Otro</Chip>
      {showOther && <input className="onb-other-input" placeholder="Cuéntanos cuál" value={other} onChange={(e) => onChange(joinChips(chips, e.target.value))} data-testid="onb-acq-other-input" />}
    </div>
  );
};

export const Step3 = ({ biz, setB, setQ, patch, toggleArr, errors }) => {
  const qs = TYPE_QUESTIONS[biz.business_type] || [];
  const ess = qs.filter((q) => q.required);
  const opt = qs.filter((q) => !q.required);
  const branch = biz.business_type === "otro" ? (biz.questionnaire.other_activity || "esta actividad") : (TYPE_ONB[biz.business_type]?.[0] || businessTypeLabel(biz.business_type)).toLowerCase();
  const toggleNet = (k) => {
    const cur = (biz.networks || []).filter((x) => x !== "sin_presencia");
    const next = k === "sin_presencia" ? ((biz.networks || []).includes(k) ? [] : [k]) : (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
    patch({ networks: next, ...(next.includes("otro") ? {} : { networks_other: "" }) });
  };
  return (
    <Page eyebrow="Tu negocio" title="Cuéntanos qué ofreces" intro="Primero recogemos lo esencial para entender tu actividad. Después puedes ampliar el contexto si quieres.">
      <div className="onb-fields">
        <div className="field wide">
          <label>Nombre del negocio o actividad <Opt /></label>
          <input data-testid="onb-business-name" placeholder="Ej.: Estudio Norte" value={biz.business_name} onChange={(e) => setB("business_name", e.target.value)} />
        </div>
        <div className="field wide onb-essential-field">
          <label>¿Qué vendes o qué servicio ofreces? <Ess /></label>
          <textarea data-testid="onb-offer" className={errors.offer ? "is-invalid" : ""} maxLength={3000} placeholder="Descríbelo con tus palabras, en una o dos frases." value={biz.offer} onChange={(e) => setB("offer", e.target.value)} />
          {!errors.offer && <span className="hint">Esto da a CaptAcción el contexto básico para entender tu actividad.</span>}
          <Err msg={errors.offer} />
        </div>
        {ess.length > 0 && (
          <div className="field wide onb-essential-block">
            <div className="onb-section-label"><b>Unos datos clave de tu actividad</b><span>Solo cambia este bloque según lo que haces.</span></div>
            <div className="onb-fields">{ess.map((q) => <Question key={q.k} q={q} biz={biz} setQ={setQ} errors={errors} essential />)}</div>
          </div>
        )}
        <details className="onb-optional wide" open data-testid="onb-more-details">
          <summary>
            <span className="onb-optional-title"><b>Más información de tu actividad</b><small>Preguntas opcionales adaptadas a {branch}.</small></span>
            <span className="optional-tag">Opcional · puedes completarlo más tarde</span>
          </summary>
          <div className="onb-optional-body">
            <p className="onb-optional-note"><i>✦</i><span>Aunque estos campos son opcionales, más contexto ayuda a CaptAcción a adaptar los perfiles, las acciones y las conversaciones a tu negocio.</span></p>
            <div className="onb-fields">
              {opt.map((q) => <Question key={q.k} q={q} biz={biz} setQ={setQ} errors={errors} />)}
              <div className="field wide" data-testid="onb-market-question">
                <label>¿En qué mercado trabajas principalmente? <Opt /></label>
                <p className="ca-market-help">Esto nos ayuda a adaptar los perfiles y las búsquedas.</p>
                <MarketField biz={biz} onPatch={patch} error={errors.market} />
              </div>
              <div className="field wide">
                <label>¿Cómo consigues oportunidades actualmente? <Opt /></label>
                <AcqChips value={biz.current_acquisition} onChange={(v) => setB("current_acquisition", v)} />
              </div>
              <div className="field wide">
                <label>¿En qué redes tienes presencia? <Opt /></label>
                <div className="onb-chips">
                  {ONB_NETWORKS.map((n) => <Chip key={n.k} on={(biz.networks || []).includes(n.k)} onClick={() => toggleNet(n.k)} testId={`onb-net-${n.k}`}>{n.label}</Chip>)}
                  <Chip on={(biz.networks || []).includes("otro")} onClick={() => toggleNet("otro")} testId="onb-net-otro">Otro</Chip>
                  {(biz.networks || []).includes("otro") && (
                    <input className="onb-other-input" placeholder="Especifica cuál" value={biz.networks_other || ""} onChange={(e) => setB("networks_other", e.target.value)} data-testid="onb-net-other-input" />
                  )}
                </div>
              </div>
              <div className="field">
                <label>¿Cómo prefieres comunicarte? <Opt /></label>
                <CaSelect testId="onb-communication" options={COMM_OPTIONS.map((c) => ({ value: c.k, label: c.label }))} value={COMM_OPTIONS.some((c) => c.k === biz.message_preference) ? biz.message_preference : ""} placeholder="Lo decidiré más adelante" onChange={(v) => setB("message_preference", v)} />
              </div>
              <div className="field">
                <label>Tiempo disponible para prospectar <Opt /></label>
                <input data-testid="onb-time" placeholder="Ej.: 2–3 horas a la semana" value={biz.time_availability} onChange={(e) => setB("time_availability", e.target.value)} />
              </div>
            </div>
          </div>
        </details>
      </div>
    </Page>
  );
};

export const Step4 = ({ biz, setB, toggleArr, errors }) => {
  const goals = biz.goals_list || [];
  const shown = GOAL_OPTIONS.filter((g) => GOAL_ONB[g.k] || goals.includes(g.k));
  return (
    <Page eyebrow="Tus objetivos" title="¿Qué quieres conseguir?" intro="Puedes elegir varios. Cada objetivo tendrá su propio perfil; así no confundimos clientes con socios, colaboradores o audiencia.">
      <div className="onb-options">
        {shown.map((g) => {
          const [t, h] = GOAL_ONB[g.k] || [g.label, ""];
          return <Option key={g.k} testId={`onb-goal-${g.k}`} selected={goals.includes(g.k)} title={t} hint={h} onClick={() => toggleArr("goals_list", g.k)} />;
        })}
      </div>
      <Err msg={errors.goals_list} />
      {needsLocalClientNote(biz) && (
        <div className="hint onb-goal-note" data-testid="onb-local-client-note">
          <p style={{ margin: 0 }}>{LOCAL_CLIENT_NOTE}</p>
          {!goals.includes(LOCAL_RECOMMENDER_GOAL) ? (
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => toggleArr("goals_list", LOCAL_RECOMMENDER_GOAL)} data-testid="onb-add-recommenders">
              ＋ Añadir «{GOAL_ONB[LOCAL_RECOMMENDER_GOAL][0]}»
            </button>
          ) : (
            <p style={{ margin: "8px 0 0" }}>✓ Hemos añadido «{GOAL_ONB[LOCAL_RECOMMENDER_GOAL][0]}». Puedes mantener también el objetivo de clientes.</p>
          )}
        </div>
      )}
      {goals.includes("reactivar") && <p className="hint onb-goal-note" data-testid="onb-existing-goal-note">Trabajaremos con tus contactos existentes; no buscaremos nuevos perfiles para este objetivo.</p>}
      <p className="hint onb-goal-note">Un referido describe cómo llega un contacto, no un perfil que haya que buscar. Por eso no aparece como objetivo independiente.</p>
    </Page>
  );
};

export const profileSummary = (a) => {
  const types = withOtherList(a.person_types || [], a.person_type_other);
  return { types: types.join(", ") || "por definir", sector: a.sector || "por definir", location: profileLocationLabel(a) };
};
const withOtherList = (l, o) => [...l.filter((x) => x !== "Otro"), ...(l.includes("Otro") && (o || "").trim() ? [o.trim()] : [])];

export const Step5 = ({ biz, setB, updateAvatar, addProfile, removeAvatar }) => {
  const goals = biz.goals_list || [];
  const searchGoals = goals.filter((g) => g !== "reactivar");
  const hasReactivar = goals.includes("reactivar");
  const onlyReactivar = hasReactivar && searchGoals.length === 0;
  const mode = biz.profile_mode || "auto";
  const list = biz.avatars_list || [];
  return (
    <Page eyebrow="Perfiles" title={onlyReactivar ? "Trabajaremos con tus contactos" : "Perfiles que quieres encontrar"}
      intro={onlyReactivar ? "Has elegido retomar contactos que ya conoces. Este objetivo no necesita perfiles de búsqueda." : "Cada perfil queda vinculado a un objetivo de búsqueda. Si eliges varios, CaptAcción los mantiene separados."}>
      {onlyReactivar ? (
        <Note testId="onb-only-reactivar">Para “Retomar contactos que ya conoces”, trabajaremos con personas que ya forman parte de tu red: no necesitamos perfiles de búsqueda ni buscaremos personas nuevas.</Note>
      ) : (
        <>
          <div className="onb-options" data-testid="onb-profile-mode">
            <Option testId="onb-mode-auto" selected={mode === "auto"} onClick={() => setB("profile_mode", "auto")} title="Que CaptAcción me proponga los perfiles"
              hint="Usará la información de tu negocio, tu oferta y los objetivos que buscan nuevas personas. Podrás revisar cada propuesta antes de buscar." />
            <Option testId="onb-mode-manual" selected={mode === "manual"} onClick={() => setB("profile_mode", "manual")} title="Crear los perfiles manualmente"
              hint="Define ahora el tipo de personas que quieres encontrar para cada objetivo de búsqueda." />
          </div>
          {mode === "auto" ? (
            <div className="onb-optional" data-testid="onb-auto-note"><div className="onb-optional-body">
              <p className="onb-optional-note"><i>✦</i><span>La propuesta automática es un borrador para revisar. No necesitas rellenar nada en este modo; puedes cambiarla antes de utilizarla.</span></p>
              {hasReactivar && <p className="onb-optional-note" data-testid="onb-reactivar-note"><i>✦</i><span>“Retomar contactos que ya conoces” queda separado y se trabajará con personas de tu red, sin proponer perfiles nuevos para ese objetivo.</span></p>}
            </div></div>
          ) : (
            <details className="onb-optional" open data-testid="onb-manual-mode">
              <summary>
                <span className="onb-optional-title"><b>Define tus perfiles por objetivo</b><small>Elige varias características compatibles; no hace falta poner nombre al perfil.</small></span>
                <span className="optional-tag">Opcional · puedes completarlo más tarde</span>
              </summary>
              <div className="onb-optional-body">
                <p className="onb-optional-note"><i>✦</i><span>Las selecciones describen varias señales compatibles del mismo público; por ejemplo, una persona emprendedora del sector salud. No se concatenan como una sola profesión.</span></p>
                {hasReactivar && <p className="onb-optional-note" data-testid="onb-reactivar-note"><i>✦</i><span>“Retomar contactos que ya conoces” se trabaja aparte con personas de tu red; no crees un perfil de búsqueda para ese objetivo.</span></p>}
                <div data-testid="onb-avatars-list">
                  {list.map((a, i) => (
                    <ProfileCard key={a.id} p={a} n={i} goals={searchGoals} onChange={updateAvatar}
                      onRemove={list.filter((x) => x.goal === a.goal).length > 1 || !searchGoals.includes(a.goal) ? () => removeAvatar(a.id) : null} />
                  ))}
                </div>
                <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => addProfile(searchGoals[0])} data-testid="onb-add-profile">＋ Añadir otro perfil</button>
              </div>
            </details>
          )}
          <p className="hint" style={{ marginTop: 15 }}>{mode === "auto" ? "La propuesta automática es un borrador: podrás revisarla y cambiarla antes de buscar." : "Cada perfil queda vinculado a su objetivo; no hace falta ponerle un nombre o etiqueta."}</p>
        </>
      )}
    </Page>
  );
};

const Article = ({ title, step, setStep, children, testId }) => (
  <article data-testid={testId}>
    <div className="onb-review-head"><b>{title}</b>{step && <button type="button" className="onb-edit" onClick={() => setStep(step)} data-testid={`${testId}-edit`}>Editar</button>}</div>
    {children}
  </article>
);

export const Step6 = ({ biz, setStep }) => {
  const goals = biz.goals_list || [];
  const searchGoals = goals.filter((g) => g !== "reactivar");
  const hasReactivar = goals.includes("reactivar");
  const typeName = biz.business_type === "otro" ? (biz.questionnaire.other_activity || "Otro") : (TYPE_ONB[biz.business_type]?.[0] || businessTypeLabel(biz.business_type));
  const nets = (biz.networks || []).map((k) => (k === "otro" ? (biz.networks_other || "").trim() || "Otro" : ONB_NETWORKS.find((n) => n.k === k)?.label || k));
  const ctx = [biz.current_acquisition && `Oportunidades: ${biz.current_acquisition}`, nets.length && `Redes: ${nets.join(", ")}`, biz.time_availability && `Tiempo: ${biz.time_availability}`].filter(Boolean);
  const auto = (biz.profile_mode || "auto") === "auto";
  return (
    <Page eyebrow="Revisión" title="Esto es lo que hemos entendido" intro="Comprueba que CaptAcción ha entendido tu actividad y cómo quieres avanzar. Verás por separado los objetivos para encontrar nuevas personas y los contactos que ya conoces.">
      <div className="onb-review" data-testid="onb-review">
        <Article title="Tu actividad" step={2} setStep={setStep} testId="onb-review-type"><span>{typeName}</span>{biz.business_name && <small>{biz.business_name}</small>}</Article>
        <Article title="Qué ofreces" step={3} setStep={setStep} testId="onb-review-offer"><span>{biz.offer || "Sin indicar"}</span></Article>
        <Article title="Mercado principal" step={3} setStep={setStep} testId="onb-review-market"><span>{marketSummary(biz)}</span></Article>
        <Article title="Qué quieres conseguir" step={4} setStep={setStep} testId="onb-review-goals"><span>{goals.map((g) => GOAL_ONB[g]?.[0] || goalLabel(g)).join(" · ") || "Sin indicar"}</span></Article>
        {searchGoals.length > 0 && (
          <Article title="Objetivos para encontrar nuevas personas" step={5} setStep={setStep} testId="onb-review-newpeople">
            <small className="onb-review-caption">{auto ? "Perfiles propuestos · podrás revisarlos antes de buscar." : "Perfiles definidos por ti · podrás ajustarlos en «Mi negocio»."}</small>
            <div className="onb-review-profile-list">
              {auto || !(biz.avatars_list || []).length
                ? searchGoals.map((g) => <div key={g} className="onb-review-profile"><b>{GOAL_ONB[g]?.[0] || goalLabel(g)}</b><span>{auto ? "CaptAcción propondrá un perfil para este objetivo." : "Sin perfil definido todavía: CaptAcción podrá proponerlo."}</span></div>)
                : biz.avatars_list.map((a, i) => { const ps = profileSummary(a); return (
                  <div key={a.id} className="onb-review-profile" data-testid={`onb-review-profile-${i}`}><b>Perfil {i + 1}: {GOAL_ONB[a.goal]?.[0] || TYPE_META[a.type]?.label || "Perfil"}</b><small>Tipo de persona: {ps.types}</small><small>Sector: {ps.sector}</small><small>Ubicación: {ps.location}</small>{a.need && <small>Necesidad: {a.need}</small>}</div>); })}
            </div>
          </Article>
        )}
        {hasReactivar && (
          <Article title="Contactos existentes" step={4} setStep={setStep} testId="onb-review-existing">
            <div className="onb-review-profile-list"><div className="onb-review-profile"><b>Retomar contactos que ya conoces</b><span>Trabajaremos con personas que ya forman parte de tu red.</span></div></div>
          </Article>
        )}
        <Article title="Contexto que tendremos en cuenta" step={3} setStep={setStep} testId="onb-review-context">
          <span>{ctx.length ? ctx.join(" · ") : "La información que compartiste ayuda a adaptar las recomendaciones."}</span>
        </Article>
      </div>
      <p className="onb-privacy" style={{ textAlign: "left" }}>Esta revisión resume el significado de tus respuestas. Podrás completarlo más tarde en «Mi negocio».</p>
    </Page>
  );
};
